import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'

import type {
  AiModule,
  ImprovementProposal,
  ModuleEvaluationRun,
  VerifiedLearningEntry,
} from '../../../app/ai-os'
import { ApiFailure } from '../../../app/errors'
import { ago, monthDayTime } from '../../../app/format'
import {
  MAINLINE,
  awaitingHumanClick,
  budgetFacts,
  clickedBy,
  isProductionVerified,
  mainlineIndex,
  riskAtLeast,
  stopReasonSentence,
} from '../../../app/module-evolution'
import type { AiOsOpOutcome } from '../../../app/ai-os-operation'
import { LearningDisable } from './disable'
import { ProposalRetry } from './retry'
import { Button } from '../../../components/button'
import { Card, CardTitle } from '../../../components/card'
import { cn } from '../../../components/cn'
import { checkValue } from '@npc/copy'

import type { CopyId } from '../../../copy'
import { t } from '../../../copy'
import { T } from '../../../copy/text'
import {
  LearningStateTag,
  ModuleStateTag,
  OutcomeTag,
  RiskTag,
  StageLine,
  VerificationTag,
} from './labels'
import { RISK_DETAIL, VERIFICATION_DETAIL } from './words'

/**
 * 项目屏里那块「我发现的可改进项」。
 *
 * # 它为什么长在项目屏里，而不是自己一屏
 *
 * 契约里 `listImprovementProposals` 那条自己写着「`project` 屏那个区块读的就是它」。
 * 而这批东西（哪个模块、跑了什么评估、验到哪一档）全都是某一个项目的东西，
 * 单开一屏就得在那屏里再选一次项目——那是把用户已经做过的选择再问一遍。
 *
 * # 界面上没有任何让状态往前走的按钮，这是设计
 *
 * 冻结合同这一组是**八读一写**，那个写只登记提案。没有 accept / execute /
 * rollback 口，所以这里也没有那几个按钮。红级提案卡在「等人点头」时，
 * 这块显示的是 `ai_os.risk.red.await_click`（「我准备好了，就等你这一下。」）
 * 加上一句 `ai_os.risk.red.refuse_auto`，**而不是一个点了没用的按钮**——
 *
 * > 一个点了没用的按钮，会让他下次不再相信按钮。（`components/state.tsx`）
 *
 * 文案合同里那条 `ai_os.action.approve_red`（「我确认，去做吧」）**接在 `retry.tsx`**，
 * 不在这一块：批红级那一下在 `POST /actions/{actionId}/approve`，第三轮契约给
 * `ImprovementProposal` 补了 `action_id`，重试被红级挡住时那里给的是一个
 * **跳过去的链接**（`action_path` 由服务端给，前端不自己拼），不是一个就地生效的按钮。
 * 这一块自己仍然没有任何批准/执行按钮，理由同上。
 *
 * # 第一屏说人话，技术证据在「看详细过程」后面
 *
 * 卡片正面只有三样：标题、`user_value`、走到哪一步了。
 * 问题证据、验收、停止条件、预算、回滚点、SHA 一律折在后面。
 */

export interface ModuleEvolutionViewProps {
  proposals: ImprovementProposal[]
  proposalsPending: boolean
  proposalsError?: unknown
  /** TanStack Query 自己重试到第几次了。0 表示没在重试。 */
  proposalsRetryCount?: number
  modules: AiModule[]
  modulesPending: boolean
  modulesError?: unknown
  moduleId: string | null
  onModuleChange: (moduleId: string | null) => void
  expandedId: string | null
  onToggleExpand: (proposalId: string | null) => void
  detail?: ImprovementProposal
  detailPending: boolean
  detailError?: unknown
  evaluations: ModuleEvaluationRun[]
  evaluationsPending: boolean
  evaluationsError?: unknown
  learning: VerifiedLearningEntry[]
  learningPending: boolean
  learningError?: unknown
  /**
   * 整个项目的体检记录，**不跟着展开的那条提案筛**。
   *
   * 与上面那个 `evaluations` 是两份：那一份是「展开的这条提案钉住的模块」的，
   * 会随展开/收起变；这一份给模块体检面板用，任何时候都是整个项目。
   * 合成一份的话，一展开提案，下面那张体检表就会悄悄只剩一个模块——
   * 而它看起来仍然像「这个项目的全部」。
   */
  healthEvaluations: ModuleEvaluationRun[]
  healthEvaluationsPending: boolean
  healthEvaluationsError?: unknown
  onRetry: () => void
  /** 上一次手动重试成功了吗。给「这次成了」那一句用。 */
  retrySucceeded?: boolean
  createSlot?: ReactNode

  // ---- 两个受控写口。**都不是执行口**：一个把提案退回流程重走，
  //      一个只改一条经验的状态 ----
  onRetryProposal: (vars: {
    proposalId: string
    idempotencyKey: string
    expectedVersion: number
    actionId?: string
  }) => void
  retryPendingId?: string
  retryOutcome?: { proposalId: string; outcome: AiOsOpOutcome; serverMessage?: string }
  onDisableLearning: (vars: {
    entryId: string
    idempotencyKey: string
    expectedVersion: number
    reason: string
  }) => void
  disablePendingId?: string
  disableOutcome?: { entryId: string; outcome: AiOsOpOutcome; serverMessage?: string }
}

export function ModuleEvolutionView(props: ModuleEvolutionViewProps) {
  const needsYou = props.proposals.filter(awaitingHumanClick).length
  const moduleOf = (id: string) => props.modules.find((m) => m.id === id)

  return (
    <Card data-testid="module-evolution">
      <div className="mb-npc-gap-lg flex flex-wrap items-start justify-between gap-npc-gap">
        <div className="min-w-0">
          <CardTitle>
            <T id="ai_os.section.title" />
          </CardTitle>
          <p className="m-0 mt-[3px] text-npc-caption text-npc-ink-soft">
            <T id="ai_os.section.subtitle" />
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-npc-gap-sm">
          {/* 「有 N 件得你点一下」。**0 件时整块不渲染**——
              「有 0 件要你点」是一句需要读两遍才知道没事的话 */}
          {needsYou > 0 ? (
            <span
              data-testid="ai-os-needs-you"
              className="rounded-npc-tag-sm bg-npc-amber-bg px-npc-tag-x py-npc-tag-y text-npc-caption-sm text-npc-amber-ink"
            >
              <T id="ai_os.section.needs_you" vars={{ count: needsYou }} />
            </span>
          ) : null}
          {props.createSlot}
        </div>
      </div>

      <ModuleFilter
        modules={props.modules}
        pending={props.modulesPending}
        error={props.modulesError}
        value={props.moduleId}
        onChange={props.onModuleChange}
      />

      {/* 整块都空：这个项目还没接进来什么东西让我盯。
          **与「有模块但没提案」是两回事**——那一档说的是「我盯着，暂时没事」，
          这一档说的是「我还没有东西可盯」，下一步完全不同 */}
      {!props.modulesPending &&
      props.modulesError === undefined &&
      props.modules.length === 0 ? (
        <div data-testid="ai-os-modules-empty" className="py-npc-grid-sm text-npc-caption text-npc-ink-soft">
          <T id="ai_os.empty.modules" />
        </div>
      ) : (
        <ListState
          pending={props.proposalsPending}
          loadingId="ai_os.loading.list"
          error={props.proposalsError}
          errorId="ai_os.error.list_failed"
          retryCount={props.proposalsRetryCount}
          retrySucceeded={props.retrySucceeded}
          empty={props.proposals.length === 0}
          /* 没筛模块 = 整个项目都没什么要改的，走区块那一句；
             筛了模块 = 只是这一块没有，走列表那一句。
             **两条模板的字目前一模一样**，这是文案合同里的一处重复，
             已回报——真到用户眼前是同一句话，所以这里不靠它们区分，
             只是把两条各归各位 */
          emptyId={props.moduleId ? 'ai_os.empty.proposals' : 'ai_os.section.empty'}
          onRetry={props.onRetry}
          testId="ai-os-proposals"
        >
          <div className="flex flex-col divide-y divide-npc-border-softer">
            {props.proposals.map((proposal) => (
              <ProposalRow
                key={proposal.id}
                proposal={proposal}
                module={moduleOf(proposal.module_id)}
                modulesLoaded={!props.modulesPending && props.modulesError === undefined}
                expanded={props.expandedId === proposal.id}
                onToggle={() =>
                  props.onToggleExpand(props.expandedId === proposal.id ? null : proposal.id)
                }
                detail={props.expandedId === proposal.id ? props.detail : undefined}
                detailPending={props.expandedId === proposal.id && props.detailPending}
                detailError={props.expandedId === proposal.id ? props.detailError : undefined}
                evaluations={props.evaluations}
                evaluationsPending={props.evaluationsPending}
                evaluationsError={props.evaluationsError}
                learning={props.learning}
                learningPending={props.learningPending}
                learningError={props.learningError}
                onRetry={props.onRetry}
                onRetryProposal={props.onRetryProposal}
                retryPendingId={props.retryPendingId}
                retryOutcome={props.retryOutcome}
                onDisableLearning={props.onDisableLearning}
                disablePendingId={props.disablePendingId}
                disableOutcome={props.disableOutcome}
              />
            ))}
          </div>
        </ListState>
      )}

      {/* 模块与体检。**放在提案下面，而且不依赖有没有提案**——
          原来模块清单与体检记录只长在「展开某条提案」后面，于是一个
          还没有任何提案的项目（今天所有真实项目都是这样，因为还没有
          东西在写体检记录）打开这一屏看到的是一片空白：既不知道我在管
          哪几块，也不知道那几块验没验过。「没有提案」与「没有东西可盯」
          是两件事，一片空白把它们混成了一件 */}
      <ModuleHealth
        modules={props.modules}
        modulesPending={props.modulesPending}
        modulesError={props.modulesError}
        evaluations={props.healthEvaluations}
        evaluationsPending={props.healthEvaluationsPending}
        evaluationsError={props.healthEvaluationsError}
        onRetry={props.onRetry}
      />
    </Card>
  )
}

/**
 * 模块与体检：我在管哪几块，每一块最近一次验成什么样。
 *
 * # 只说验到的，不说「还行」
 *
 * 一个模块**没有体检记录**时，这里说的是「这件事我还没开始验」，
 * 不是「正常」「健康」这类话。两者在用户那儿的下一步完全不同：
 * 前者要去看为什么没验，后者可以放心走开。而把「没量过」显示成
 * 「没问题」，正是这一整套东西存在的理由的反面。
 *
 * # 取最近那一次，而且取的是**跑完了**的那一次
 *
 * 一条还在跑的评估代表不了这个模块现在的样子。按 finished_at 挑，
 * 没有 finished_at 的不参与评选。
 */
function ModuleHealth({
  modules,
  modulesPending,
  modulesError,
  evaluations,
  evaluationsPending,
  evaluationsError,
  onRetry,
}: {
  modules: AiModule[]
  modulesPending: boolean
  modulesError?: unknown
  evaluations: ModuleEvaluationRun[]
  evaluationsPending: boolean
  evaluationsError?: unknown
  onRetry: () => void
}) {
  return (
    <Section heading="ai_os.module.heading">
      <ListState
        pending={modulesPending || evaluationsPending}
        loadingId="ai_os.loading.list"
        error={modulesError ?? evaluationsError}
        errorId="ai_os.error.list_failed"
        empty={modules.length === 0}
        emptyId="ai_os.empty.modules"
        onRetry={onRetry}
        testId="ai-os-module-health"
      >
        <div className="flex flex-col gap-npc-gap-sm">
          {modules.map((module) => {
            const latest = latestFinishedRun(evaluations, module.id)
            return (
              <div
                key={module.id}
                data-testid="ai-os-module-health-row"
                data-module-id={module.id}
                className="rounded-npc-inset border border-npc-border-soft bg-npc-panel px-npc-grid-sm py-npc-row-y"
              >
                <ModuleRow module={module} />
                {latest ? (
                  <div className="mt-npc-gap-xs">
                    <EvaluationRow run={latest} frozen={false} />
                  </div>
                ) : (
                  /* **没验过就说没验过。** 这一行不是装饰：
                     它是「无证据不显示已进化」在这一屏上的样子 */
                  <p
                    data-testid="ai-os-module-no-evidence"
                    className="m-0 mt-npc-gap-xs text-npc-caption text-npc-ink-soft"
                  >
                    <T id="ai_os.empty.evaluations" />
                  </p>
                )}
              </div>
            )
          })}
        </div>
      </ListState>
    </Section>
  )
}

/**
 * 这个模块最近一次**跑完了**的评估。
 *
 * 没跑完的不参与评选——一条还在跑的评估说不出这个模块现在什么样，
 * 而拿它当「最近一次」，会让一个正在验的模块看起来已经有结论了。
 */
function latestFinishedRun(
  runs: ModuleEvaluationRun[],
  moduleId: string,
): ModuleEvaluationRun | undefined {
  let best: ModuleEvaluationRun | undefined
  for (const run of runs) {
    if (run.module_id !== moduleId || !run.finished_at) continue
    // Date.parse 读不出来时给 NaN，而任何与 NaN 的比较都是 false——
    // 一条日期不合法的记录会就此占住 best，**后面每一条合法的都换不掉它**。
    // 于是「最近一次」显示的是一条烂数据，而界面上看不出哪里不对。
    const at = Date.parse(run.finished_at)
    if (Number.isNaN(at)) continue
    const bestAt = best ? Date.parse(best.finished_at as string) : Number.NEGATIVE_INFINITY
    if (!best || Number.isNaN(bestAt) || at > bestAt) best = run
  }
  return best
}

/**
 * 加载 / 出错 / 空 / 重试，四条列表共用。
 *
 * 顺序是定死的：**加载 → 出错 → 空 → 有数据**。把「空」排在「出错」前面的话，
 * 一次失败的请求会显示成「这个项目现在没什么要改的」——
 * **那是一句好消息，说给一个其实什么都没取到的人听。**
 *
 * 重试那几句分得很细，因为它们的下一步不一样：
 *
 *   `ai_os.retry.auto`      我自己还在试，**你什么都不用做**
 *   `ai_os.retry.gave_up`   我不试了，**等你看一眼**
 *   `ai_os.retry.manual_ok` 这次成了
 */
function ListState({
  pending,
  loadingId,
  error,
  errorId,
  retryCount = 0,
  retrySucceeded = false,
  empty,
  emptyId,
  onRetry,
  testId,
  children,
}: {
  pending: boolean
  loadingId: CopyId
  error?: unknown
  errorId: CopyId
  retryCount?: number
  retrySucceeded?: boolean
  empty: boolean
  emptyId?: CopyId
  onRetry: () => void
  testId: string
  children: ReactNode
}) {
  if (pending) {
    return (
      <div aria-busy="true" data-testid={`${testId}-loading`} className="flex flex-col gap-npc-gap-sm">
        <div className="text-npc-caption text-npc-ink-soft">
          {/* 还在自己重试的时候说的是另一句：「我自己再试一次，第 N 次」——
              它与「我在翻记录」的区别是**上一次已经失败过了**，
              而用户看到同一句话会以为一切正常 */}
          {retryCount > 0 ? (
            <span data-testid={`${testId}-retrying`}>
              <T id="ai_os.retry.auto" vars={{ attempt: retryCount }} />
            </span>
          ) : (
            <T id={loadingId} />
          )}
        </div>
        {[0, 1].map((i) => (
          <div key={i} className="h-[52px] animate-npc-pulse rounded-npc-inset bg-npc-surface" />
        ))}
      </div>
    )
  }

  if (error !== undefined && error !== null) {
    return (
      <ListError
        error={error}
        errorId={errorId}
        retryCount={retryCount}
        onRetry={onRetry}
        testId={testId}
      />
    )
  }

  if (empty) {
    if (!emptyId) return null
    return (
      <div data-testid={`${testId}-empty`} className="py-npc-grid-sm text-npc-caption text-npc-ink-soft">
        <T id={emptyId} />
      </div>
    )
  }

  return (
    <>
      {retrySucceeded ? (
        <div data-testid={`${testId}-retry-ok`} className="mb-npc-gap-sm text-npc-caption text-npc-green-deep">
          <T id="ai_os.retry.manual_ok" />
        </div>
      ) : null}
      {children}
    </>
  )
}

/**
 * 一条列表取不到时说什么。
 *
 * `missing` 那一档单独走：后端还没挂这一组接口，这时候**不能承诺重试**——
 * 一条不存在的路由重试一万次还是不存在。判据是 `ApiFailure.kind`，
 * 不是去翻那句话的字。
 *
 * 剩下的两档也分开：自己试过又放弃了（说清试了几次，等你看一眼），
 * 与第一次就失败（我过一会儿自己再试）。
 */
function ListError({
  error,
  errorId,
  retryCount,
  onRetry,
  testId,
}: {
  error: unknown
  errorId: CopyId
  retryCount: number
  onRetry: () => void
  testId: string
}) {
  const kind = error instanceof ApiFailure ? error.kind : 'reported'

  if (kind === 'missing') {
    return (
      <div
        data-testid={`${testId}-missing`}
        className="rounded-npc-inset border border-npc-border bg-npc-surface px-npc-grid-sm py-npc-gap-sm text-npc-caption text-npc-ink-soft"
      >
        <T id="ui.not_ready" />
      </div>
    )
  }

  return (
    <div
      data-testid={`${testId}-error`}
      className="flex flex-wrap items-center justify-between gap-npc-gap rounded-npc-inset border border-npc-amber-border bg-npc-amber-bg px-npc-grid-sm py-npc-gap-sm text-npc-caption text-npc-amber-ink"
    >
      <span className="min-w-0">
        {retryCount > 0 ? (
          <span data-testid={`${testId}-gave-up`}>
            <T id="ai_os.retry.gave_up" vars={{ attempt: retryCount }} />
          </span>
        ) : kind === 'reported' && error instanceof Error && error.message ? (
          /* **后端按契约说了话，就照原样用它那句。**
             它是后端用 docs/copy 的模板渲染好的，比这边这条泛用的准。
             前端再包装一次就是两份真相（`app/errors.ts` 里那条规矩）。
             `ai_os.error.list_failed` 留给「后端根本没说话」那一档——
             连不上、反代答的、拿到了一个 index.html */
          error.message
        ) : (
          <T id={errorId} />
        )}
      </span>
      <Button size="sm" onClick={onRetry} data-testid={`${testId}-retry`}>
        <T id="ai_os.action.retry" />
      </Button>
    </div>
  )
}

/**
 * 按模块筛。
 *
 * 原生 `<select>`：选项是一串模块名，没有富交互，而原生 select 在手机上
 * 直接是系统选择器，比自己搭一个下拉靠谱。
 */
function ModuleFilter({
  modules,
  pending,
  error,
  value,
  onChange,
}: {
  modules: AiModule[]
  pending: boolean
  error?: unknown
  value: string | null
  onChange: (moduleId: string | null) => void
}) {
  if (pending || error !== undefined || modules.length === 0) return null

  return (
    <div className="mb-npc-gap-lg flex items-center gap-npc-gap-sm">
      <select
        data-testid="ai-os-module-filter"
        aria-label={t('ai_os.module.heading')}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        className="rounded-npc-inset border border-npc-border-soft bg-npc-panel px-npc-gap-sm py-npc-gap-xs text-npc-caption text-npc-ink"
      >
        <option value="">{t('ai_os.module.heading')}</option>
        {modules.map((m) => (
          <option key={m.id} value={m.id}>
            {m.display_name}
          </option>
        ))}
      </select>
    </div>
  )
}

function ProposalRow({
  proposal,
  module,
  modulesLoaded,
  expanded,
  onToggle,
  detail,
  detailPending,
  detailError,
  evaluations,
  evaluationsPending,
  evaluationsError,
  learning,
  learningPending,
  learningError,
  onRetry,
  onRetryProposal,
  retryPendingId,
  retryOutcome,
  onDisableLearning,
  disablePendingId,
  disableOutcome,
}: {
  proposal: ImprovementProposal
  module?: AiModule
  modulesLoaded: boolean
  expanded: boolean
  onToggle: () => void
  detail?: ImprovementProposal
  detailPending: boolean
  detailError?: unknown
  evaluations: ModuleEvaluationRun[]
  evaluationsPending: boolean
  evaluationsError?: unknown
  learning: VerifiedLearningEntry[]
  learningPending: boolean
  learningError?: unknown
  onRetry: () => void
  onRetryProposal: ModuleEvolutionViewProps['onRetryProposal']
  retryPendingId?: string
  retryOutcome?: ModuleEvolutionViewProps['retryOutcome']
  onDisableLearning: ModuleEvolutionViewProps['onDisableLearning']
  disablePendingId?: string
  disableOutcome?: ModuleEvolutionViewProps['disableOutcome']
}) {
  return (
    <div className="py-npc-row-y" data-testid="ai-os-proposal">
      <div className="flex flex-wrap items-start justify-between gap-npc-gap">
        <div className="min-w-0 flex-1">
          <div className="text-npc-body-sm font-medium leading-[1.45] text-npc-ink">
            {proposal.title}
          </div>
          {/* 做完之后谁会感觉到不一样。契约把它列成必填，理由是
              「写不出来的提案多半在描述一个技术痒点」 */}
          <p className="m-0 mt-[3px] text-npc-caption leading-[1.6] text-npc-ink-soft">
            <Benefit value={proposal.user_value} />
          </p>
          {/* **每条都要说清影响谁**（CLAUDE.md §3）。这一项是契约第三轮新加的
              必填列——在那之前界面只能自己编一个「大约 40 个客人」，
              而编出来的那个数字最不该出现在这儿 */}
          <p data-testid="ai-os-impact" className="m-0 mt-[2px] text-npc-meta text-npc-ink-softer">
            <T id="ai_os.proposal.impact" vars={{ who: proposal.impact_audience }} />
          </p>
        </div>
        <RiskTag risk={proposal.risk_level} />
      </div>

      <div className="mt-npc-gap-sm flex flex-wrap items-center gap-npc-gap-sm">
        <StageLine state={proposal.state} stageLabel={proposal.stage_label} />
        <MainlineProgress state={proposal.state} />
        <time
          dateTime={proposal.updated_at}
          className="font-npc-mono text-npc-meta-sm text-npc-ink-softer"
        >
          {ago(proposal.updated_at)}
        </time>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          data-testid="ai-os-proposal-toggle"
          className="text-npc-caption text-npc-ink-soft hover:text-npc-ink"
        >
          <T id="ai_os.section.detail_link" />
        </button>
      </div>

      <HumanClickGate proposal={proposal} module={module} modulesLoaded={modulesLoaded} />

      <StopReason proposal={proposal} moduleName={module?.display_name} />

      <ProposalRetry
        proposal={proposal}
        pending={retryPendingId === proposal.id}
        outcome={retryOutcome?.proposalId === proposal.id ? retryOutcome.outcome : undefined}
        serverMessage={retryOutcome?.proposalId === proposal.id ? retryOutcome.serverMessage : undefined}
        onRetry={(vars) => onRetryProposal({ proposalId: proposal.id, ...vars })}
      />

      {expanded ? (
        <div className="mt-npc-gap-lg rounded-npc-inset border border-npc-border-soft bg-npc-surface-warm p-npc-card-sm">
          <ListState
            pending={detailPending}
            loadingId="ai_os.loading.detail"
            error={detailError}
            errorId="ai_os.error.detail_failed"
            empty={!detail}
            onRetry={onRetry}
            testId="ai-os-detail"
          >
            {detail ? (
              <ProposalDetail
                detail={detail}
                listVersion={proposal.version}
                module={module}
                onDisableLearning={onDisableLearning}
                disablePendingId={disablePendingId}
                disableOutcome={disableOutcome}
                evaluations={evaluations}
                evaluationsPending={evaluationsPending}
                evaluationsError={evaluationsError}
                learning={learning}
                learningPending={learningPending}
                learningError={learningError}
                onRetry={onRetry}
              />
            ) : null}
          </ListState>
        </div>
      ) : null}
    </div>
  )
}

/**
 * 为什么停下来。
 *
 * **整句由服务端那条 `stop_reason_copy_id` 决定，前端不做映射。**
 * 认不得那个 id（版本错位、模板改名、后端填错）就整块不渲染——
 * 尤其**绝不把 `stop_reason` 那个枚举名印出来**：`budget_exhausted`
 * 六个字母对用户什么都没说，而它看起来像是界面坏了。
 * 判据在 `app/module-evolution.ts` 的 `stopReasonSentence`。
 *
 * `budget_exhausted` 那一档另外带两个数。那句模板只说「花超了」，
 * **而用户想知道的是超了多少**——两个数都在且都是数字才显示，
 * 半截的对比比不显示更糟。
 */
function StopReason({
  proposal,
  moduleName,
}: {
  proposal: ImprovementProposal
  moduleName?: string
}) {
  const budget = budgetFacts(proposal)
  /**
   * `blocked_by_red` 那一档**这里不说**，交给重试那一块。
   *
   * 两处都渲染的话，同一张卡上会出现两遍
   * 「这件事碰到钱了，重试也得你先点一下」——预览图上真的是这样。
   * 而那两遍不是等价的：**重试那一块的后面跟着一个能点过去的链接**，
   * 这里的没有。同一句话说两遍，其中一遍还是死的，比只说一遍差。
   */
  const sentence =
    proposal.stop_reason === 'blocked_by_red' ? null : stopReasonSentence(proposal, { moduleName })
  if (!sentence && !budget) return null

  return (
    <div
      data-testid="ai-os-stop-reason"
      className="mt-npc-gap-sm rounded-npc-inset border border-npc-border-soft bg-npc-surface px-npc-grid-sm py-npc-gap-sm text-npc-caption text-npc-ink-medium"
    >
      {sentence ? <p className="m-0">{sentence}</p> : null}
      {budget ? (
        <p data-testid="ai-os-budget-facts" className="m-0 mt-[2px] font-npc-mono text-npc-meta-sm text-npc-ink-softer">
          {budget.consumed} / {budget.limit}
        </p>
      ) : null}
    </div>
  )
}

/**
 * 主链走到第几格。
 *
 * 十格小方块，不写字——字由 `StageLine` 那一句说。
 * 不在主链上的四档整块不渲染：给它们画一条进度条会读成「还在往前走」，
 * 而 `failed` / `rolled_back` 不是。
 */
/**
 * 「做完之后 {benefit}」。
 *
 * `user_value` 是用户自己写的一段，而表单那条提示写着「用大白话写，
 * 比如『下单卡住的情况会消失』」——**它本来就是接在「做完之后」后面的半句**。
 *
 * 但它是**用户输入**，而 `render` 对变量值是 fail closed 的：空、控制符、
 * 超过 200 字、emoji、术语黑名单，任一不过就抛 `CopyError`。
 * 直接塞进去的话，一条写得太长的提案会把整块界面炸掉。
 *
 * 所以先用 `checkValue`（就是 `render` 内部那一道）问一句：过得了就套模板，
 * 过不了就原样显示他写的那段。**原样显示不是降级，是另一种正确**——
 * 那段话是他自己写的，我们没有替他改写的理由。
 */
function Benefit({ value }: { value: string }) {
  if (checkValue('benefit', value) === null) {
    return <T id="ai_os.proposal.why_worth_it" vars={{ benefit: value }} />
  }
  return <>{value}</>
}

function MainlineProgress({ state }: { state: ImprovementProposal['state'] }) {
  const index = mainlineIndex(state)
  if (index < 0) return null

  return (
    <span
      aria-hidden="true"
      data-testid="ai-os-mainline"
      data-index={index}
      className="inline-flex items-center gap-[3px]"
    >
      {MAINLINE.map((_, i) => (
        <span
          key={i}
          className={cn(
            'h-[4px] w-[10px] rounded-npc-inline',
            i <= index ? 'bg-npc-green' : 'bg-npc-border',
          )}
        />
      ))}
    </span>
  )
}

/**
 * 人点那道门，以及「这条比我被允许做的还大」。
 *
 * 三件事，**说的是三个不同的下一步**：
 *
 *   等着人点     `requires_human_click` 为真而 `human_clicked_at` 还空着
 *   超出模块上界  提案的风险高过模块的 `max_risk`——我停在这儿
 *   我自己就办了  不要求人点
 *
 * 「已经点过了」那一档**第三轮补上了**：契约加了 `human_clicked_by`，
 * `ai_os.risk.red.clicked` 要的那个 `{who}` 有了出处，下面 `clickedBy`
 * 两样齐了才渲染。上一版这里写着「没做」，是那时契约只有 `human_clicked_at`——
 * 一句「在某时点过了」而说不出是谁，在事后追责时与「有人偷偷点的」无法区分。
 */
function HumanClickGate({
  proposal,
  module,
  modulesLoaded,
}: {
  proposal: ImprovementProposal
  module?: AiModule
  modulesLoaded: boolean
}) {
  const waiting = awaitingHumanClick(proposal)
  // 模块还没取到时不下这个判断：`module` 是 undefined 只说明「还不知道」，
  // 而按「不知道」渲染成「没超上界」是把一句结论说给一个没有依据的人听
  const overCap = Boolean(module) && riskAtLeast(proposal.risk_level, module!.max_risk) &&
    proposal.risk_level !== module!.max_risk

  if (!waiting && !overCap) {
    // **点过了，而且说得出是谁点的。** 这一句上一版整块没做——
    // 那时契约只有 `human_clicked_at`，一句「在某时点过了」而说不出是谁，
    // 在事后追责时与「有人偷偷点的」无法区分。第三轮补了 `human_clicked_by`，
    // 两样齐了才显示（`clickedBy` 少一样就回 null）
    const clicked = clickedBy(proposal)
    if (clicked) {
      return (
        <p
          data-testid="ai-os-click-done"
          className="m-0 mt-npc-gap-sm text-npc-meta text-npc-green-deep"
        >
          <T
            id="ai_os.risk.red.clicked"
            vars={{ who: clicked.who, when: monthDayTime(clicked.when) }}
          />
        </p>
      )
    }
    // 不要求人点的那一条：说清「这条我会自己做完」。
    // 与「等你点」是两件事，合成一句会让用户以为自己漏点了什么
    if (!proposal.requires_human_click) {
      return (
        <p data-testid="ai-os-auto-note" className="m-0 mt-npc-gap-sm text-npc-meta text-npc-ink-softer">
          <T id="ai_os.proposal.auto_note" />
        </p>
      )
    }
    return null
  }

  return (
    <div
      data-testid="ai-os-awaiting-click"
      className="mt-npc-gap-sm rounded-npc-inset border border-npc-amber-border bg-npc-amber-bg px-npc-grid-sm py-npc-gap-sm text-npc-caption text-npc-amber-ink"
    >
      {waiting ? (
        <p className="m-0">
          <T id="ai_os.risk.red.await_click" />
        </p>
      ) : null}
      {waiting && proposal.risk_level === 'red' ? (
        <p className="m-0 mt-[2px] text-npc-ink-softer">
          <T id="ai_os.risk.red.refuse_auto" />
        </p>
      ) : null}
      {overCap && modulesLoaded && module ? (
        <p data-testid="ai-os-over-cap" className="m-0 mt-[2px]">
          <T id="ai_os.risk.over_module_cap" vars={{ name: module.display_name }} />
        </p>
      ) : null}
    </div>
  )
}

/** 「看详细过程」后面那些。技术证据全在这儿。 */
function ProposalDetail({
  detail,
  listVersion,
  module,
  onDisableLearning,
  disablePendingId,
  disableOutcome,
  evaluations,
  evaluationsPending,
  evaluationsError,
  learning,
  learningPending,
  learningError,
  onRetry,
}: {
  detail: ImprovementProposal
  listVersion?: number
  module?: AiModule
  onDisableLearning: ModuleEvolutionViewProps['onDisableLearning']
  disablePendingId?: string
  disableOutcome?: ModuleEvolutionViewProps['disableOutcome']
  evaluations: ModuleEvaluationRun[]
  evaluationsPending: boolean
  evaluationsError?: unknown
  learning: VerifiedLearningEntry[]
  learningPending: boolean
  learningError?: unknown
  onRetry: () => void
}) {
  // 列表那份与详情这份的 `version` 对不上 = 你看的时候它已经被改过了。
  // **不是错误**，所以不走红底：详情这份是新的，提示一句让他再看一眼就够
  const changedElsewhere =
    listVersion !== undefined && detail.version !== undefined && detail.version !== listVersion

  const productionVerified = evaluations.some(isProductionVerified)
  // 被学习项引用过的评估，结论就冻住了——复合外键刻意没有 ON UPDATE CASCADE
  const frozenRunIds = new Set(learning.map((e) => e.evaluation_run_id))

  return (
    <div data-testid="ai-os-proposal-detail" className="flex flex-col gap-npc-gap-lg">
      {changedElsewhere ? (
        <p
          data-testid="ai-os-changed-elsewhere"
          className="m-0 rounded-npc-inset border border-npc-blue bg-npc-blue-bg px-npc-grid-sm py-npc-gap-sm text-npc-caption text-npc-blue-ink"
        >
          <T id="ai_os.conflict.changed_elsewhere" />
        </p>
      ) : null}

      <p className="m-0 text-npc-meta text-npc-ink-softer">
        <T id="ai_os.proposal.found_at" vars={{ when: monthDayTime(detail.created_at ?? detail.updated_at) }} />
      </p>

      {module ? (
        <div className="flex flex-wrap items-center gap-npc-gap-sm">
          <span className="text-npc-caption text-npc-ink-medium">
            <T id="ai_os.module.owner" vars={{ name: module.display_name, owner: module.owner_stream }} />
          </span>
          <ModuleStateTag state={module.state} />
          {/* 说不出怎么退，就不该自动改。**空对象不算一个回滚点** */}
          {!module.rollback || Object.keys(module.rollback).length === 0 ? (
            <span data-testid="ai-os-module-no-rollback" className="text-npc-caption text-npc-amber-ink">
              <T id="ai_os.module.no_rollback" />
            </span>
          ) : null}
        </div>
      ) : null}

      <Section heading="ai_os.section.evidence_heading">
        <JsonList items={detail.problem_evidence} testId="ai-os-evidence" />
      </Section>

      <Section heading="ai_os.section.plan_heading">
        <p className="m-0">{detail.goal}</p>
        {detail.uncertainty ? (
          <p data-testid="ai-os-uncertain" className="m-0 mt-npc-gap-xs text-npc-amber-ink">
            <T id="ai_os.proposal.uncertain" />
          </p>
        ) : null}
        {/* 被接受之后挂上的开发任务。两档说的是两件事：
            派过了（点得进去看）与还没派（等这条定下来） */}
        {detail.development_task_id ? (
          <Link
            to="/development-tasks/$taskId"
            params={{ taskId: detail.development_task_id }}
            data-testid="ai-os-task-link"
            className="mt-npc-gap-xs inline-block text-npc-caption text-npc-blue-ink hover:underline"
          >
            <T id="ai_os.proposal.task_link" />
          </Link>
        ) : (
          <p data-testid="ai-os-no-task" className="m-0 mt-npc-gap-xs text-npc-meta text-npc-ink-softer">
            <T id="ai_os.proposal.no_task_yet" />
          </p>
        )}
      </Section>

      <Section heading="ai_os.section.acceptance_heading">
        <JsonList items={detail.acceptance} testId="ai-os-acceptance" />
      </Section>

      <Section heading="ai_os.section.rollback_heading">
        <Json value={detail.rollback} />
        <div className="mt-npc-gap-xs">
          <FieldLabel id="ai_os.field.stop_conditions" />
          <JsonList items={detail.stop_conditions} testId="ai-os-stop" />
        </div>
      </Section>

      <Section heading="ai_os.section.budget_heading">
        <Json value={detail.budget} />
        {detail.forbidden_boundaries && detail.forbidden_boundaries.length > 0 ? (
          <div className="mt-npc-gap-xs">
            <FieldLabel id="ai_os.field.forbidden_boundaries" />
            <ul className="m-0 list-none p-0">
              {detail.forbidden_boundaries.map((b) => (
                <li key={b} className="font-npc-mono text-npc-meta-sm">
                  {b}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <div className="mt-npc-gap-xs">
          <FieldLabel id="ai_os.field.owner_stream" />
          <span className="font-npc-mono text-npc-meta-sm">{detail.owner_stream}</span>
        </div>
      </Section>

      <Section heading="ai_os.evidence.heading">
        {/* 「这件事验到哪一步了」是整块的标题；
            「我验了哪些」是下面那串跑过的用例。两句说的不是一件事：
            前者答等级，后者答清单 */}
        <div className="mb-npc-gap-sm text-npc-meta text-npc-ink-softer">
          <T id="ai_os.eval.heading" />
        </div>
        <ListState
          pending={evaluationsPending}
          loadingId="ai_os.loading.evidence"
          error={evaluationsError}
          errorId="ai_os.error.list_failed"
          empty={evaluations.length === 0}
          emptyId="ai_os.empty.evaluations"
          onRetry={onRetry}
          testId="ai-os-evaluations"
        >
          <div className="flex flex-col gap-npc-gap-sm">
            {evaluations.map((run) => (
              <EvaluationRow key={run.id} run={run} frozen={frozenRunIds.has(run.id)} />
            ))}
          </div>
        </ListState>
        {/* 一条都没到 production_verified 时说清「所以这条我不会记成经验」——
            用户看到一堆绿色的「都过了」，很容易以为它已经算数了 */}
        {!evaluationsPending && evaluations.length > 0 && !productionVerified ? (
          <p
            data-testid="ai-os-not-enough-to-learn"
            className="m-0 mt-npc-gap-sm text-npc-caption text-npc-ink-soft"
          >
            <T id="ai_os.evidence.not_enough_to_learn" />
          </p>
        ) : null}
      </Section>

      <Section heading="ai_os.section.learning_heading" subtitle="ai_os.section.learning_subtitle">
        <ListState
          pending={learningPending}
          loadingId="ai_os.loading.evidence"
          error={learningError}
          errorId="ai_os.error.list_failed"
          empty={learning.length === 0}
          emptyId="ai_os.empty.learning"
          onRetry={onRetry}
          testId="ai-os-learning"
        >
          <div className="flex flex-col gap-npc-gap-sm">
            {learning.map((entry) => (
              <LearningRow
                key={entry.id}
                entry={entry}
                onDisable={(vars) => onDisableLearning({ entryId: entry.id, ...vars })}
                pending={disablePendingId === entry.id}
                outcome={disableOutcome?.entryId === entry.id ? disableOutcome.outcome : undefined}
                serverMessage={
                  disableOutcome?.entryId === entry.id ? disableOutcome.serverMessage : undefined
                }
              />
            ))}
          </div>
        </ListState>
      </Section>
    </div>
  )
}

function Section({
  heading,
  subtitle,
  children,
}: {
  heading: CopyId
  subtitle?: CopyId
  children: ReactNode
}) {
  return (
    <section>
      <div className="mb-npc-gap-sm text-npc-caption-sm font-semibold text-npc-ink">
        <T id={heading} />
      </div>
      {subtitle ? (
        <p className="m-0 mb-npc-gap-sm text-npc-meta text-npc-ink-softer">
          <T id={subtitle} />
        </p>
      ) : null}
      <div className="text-npc-caption leading-[1.6] text-npc-ink-medium">{children}</div>
    </section>
  )
}

function FieldLabel({ id }: { id: CopyId }) {
  return (
    <div className="text-npc-meta text-npc-ink-softer">
      <T id={id} />
    </div>
  )
}

/**
 * 一次评估。
 *
 * **`verification` 照抄接口那一档，一处都不推导。** 想在这儿写
 * 「environment 是 production 而且 passed，那就是验过了」——
 * 那正是这一屏要挡住的事：那个结论的唯一合法来源是库里那条 CHECK。
 */
function EvaluationRow({ run, frozen }: { run: ModuleEvaluationRun; frozen: boolean }) {
  const compares = comparableMetrics(run)
  const notBlocked = negativeCasesNotBlocked(run)

  return (
    <div
      data-testid="ai-os-evaluation"
      className="rounded-npc-inset border border-npc-border-soft bg-npc-panel px-npc-grid-sm py-npc-row-y"
    >
      <div className="flex flex-wrap items-center gap-npc-gap-xs">
        <span className="font-npc-mono text-npc-meta-sm text-npc-ink">{run.suite_key}</span>
        <VerificationTag verification={run.verification} />
        <OutcomeTag outcome={run.outcome} />
      </div>

      <p className="m-0 mt-npc-gap-xs text-npc-meta leading-[1.6] text-npc-ink-softer">
        <T id={VERIFICATION_DETAIL[run.verification]} />
      </p>

      {/* 这条证据已经有经验靠着它了，结论不会再改 */}
      {frozen ? (
        <p data-testid="ai-os-evidence-frozen" className="m-0 mt-npc-gap-xs text-npc-meta text-npc-ink-softer">
          <T id="ai_os.evidence.frozen" />
        </p>
      ) : null}

      {compares.map(({ key, before, after }) => (
        <p key={key} className="m-0 mt-npc-gap-xs text-npc-caption" data-testid="ai-os-eval-compare">
          <T id="ai_os.eval.compare" vars={{ before: `${key} ${before}`, after: String(after) }} />
        </p>
      ))}

      {run.negative_cases && run.negative_cases.length > 0 ? (
        <p className="m-0 mt-npc-gap-xs text-npc-caption" data-testid="ai-os-negative-cases">
          {notBlocked > 0 ? (
            <T id="ai_os.eval.negative_cases_failed" vars={{ count: notBlocked }} />
          ) : (
            <T id="ai_os.eval.negative_cases" vars={{ count: run.negative_cases.length }} />
          )}
        </p>
      ) : null}

      <div className="mt-npc-gap-xs font-npc-mono text-npc-meta-sm text-npc-ink-softer">
        {run.candidate_sha.slice(0, 12)}
        {run.evidence_source.trim() ? ` · ${run.evidence_source}` : null}
      </div>
    </div>
  )
}

/** 一条已验经验。 */
function LearningRow({
  entry,
  onDisable,
  pending,
  outcome,
  serverMessage,
}: {
  entry: VerifiedLearningEntry
  onDisable: (vars: { idempotencyKey: string; expectedVersion: number; reason: string }) => void
  pending: boolean
  outcome?: AiOsOpOutcome
  serverMessage?: string
}) {
  return (
    <div
      data-testid="ai-os-learning-entry"
      className="rounded-npc-inset border border-npc-border-soft bg-npc-panel px-npc-grid-sm py-npc-row-y"
    >
      <div className="flex flex-wrap items-center gap-npc-gap-xs">
        {/* 现在还生效吗 = 接口回的 active。**前端不拿 expires_at 自己算**：
            那个判断在服务端，两边时钟差几秒就会给出两个不同的答案 */}
        {entry.active ? (
          <Tagline testId="ai-os-learning-active" id="ai_os.learning.active" />
        ) : null}
        <LearningStateTag state={entry.state} />
        <span className="font-npc-mono text-npc-meta-sm text-npc-ink-softer">
          {entry.applied_sha.slice(0, 12)}
        </span>
      </div>

      <p className="m-0 mt-npc-gap-xs text-npc-caption text-npc-ink-medium">
        <T id="ai_os.learning.scope" vars={{ where: describe(entry.scope) }} />
      </p>
      <p className="m-0 mt-[2px] text-npc-meta text-npc-ink-softer">
        <T id="ai_os.learning.expires" vars={{ when: monthDayTime(entry.expires_at) }} />
      </p>

      {/* 禁用了就要说清为什么。不说的话，下一个看到它的人只会把它再打开 */}
      {/* 「什么时候关、为什么关、谁关的」三样一起要（库里 CHECK 兜着）。
          界面这一侧只显示为什么——**但少了 reason 就整块不显示**，
          一条「被关掉但说不出为什么」的经验，下一个看到它的人只会把它再打开 */}
      {entry.state === 'disabled' && entry.disabled_reason ? (
        <p data-testid="ai-os-learning-disabled" className="m-0 mt-[2px] text-npc-caption text-npc-amber-ink">
          <T id="ai_os.learning.disabled" vars={{ reason: entry.disabled_reason }} />
        </p>
      ) : null}

      <details className="mt-npc-gap-xs">
        <summary className="cursor-pointer text-npc-caption text-npc-ink-soft">
          <T id="ai_os.action.view_evidence" />
        </summary>
        <div className="mt-npc-gap-xs">
          <FieldLabel id="ai_os.field.problem_evidence" />
          <JsonList items={entry.invalidation_conditions} testId="ai-os-invalidation" />
          <div className="font-npc-mono text-npc-meta-sm text-npc-ink-softer">
            {entry.evidence_fingerprint.slice(0, 12)}
          </div>
        </div>
      </details>

      {/* 「别再用这条经验」。**它不是回滚**——只改这一行的状态，
          不把照它办过的事撤回来。两件事的下一步完全不同 */}
      <LearningDisable
        entry={entry}
        onDisable={onDisable}
        pending={pending}
        outcome={outcome}
        serverMessage={serverMessage}
      />
    </div>
  )
}

function Tagline({ testId, id }: { testId: string; id: CopyId }) {
  return (
    <span
      data-testid={testId}
      className="rounded-npc-tag-sm bg-npc-green-bg px-npc-tag-x py-npc-tag-y text-npc-caption-sm text-npc-green-deep"
    >
      <T id={id} />
    </span>
  )
}

/** 模块那一行。给模块列表用。 */
export function ModuleRow({ module }: { module: AiModule }) {
  return (
    <div
      data-testid="ai-os-module"
      className="rounded-npc-inset border border-npc-border-soft px-npc-grid-sm py-npc-row-y"
    >
      <div className="flex flex-wrap items-center gap-npc-gap-xs">
        <span className="text-npc-body-sm font-medium text-npc-ink">{module.display_name}</span>
        <ModuleStateTag state={module.state} />
        <RiskTag risk={module.max_risk} />
      </div>
      <p className="m-0 mt-[2px] text-npc-meta text-npc-ink-softer">
        <T id={RISK_DETAIL[module.max_risk]} />
      </p>
    </div>
  )
}

/**
 * `baseline_metrics` 与 `candidate_metrics` 里**两边都有的那几个数**。
 *
 * 只取两边都有、而且都是数字的键。契约里这两个字段是
 * `additionalProperties: true` 的自由对象——**形状是刻意没定的**，
 * 所以这里不猜结构，只挑能确定比得了的那几个。
 * 比不了的一个都不显示，而不是硬拼一句「改之前 {一坨 JSON}」。
 */
function comparableMetrics(run: ModuleEvaluationRun): { key: string; before: number; after: number }[] {
  const before = run.baseline_metrics
  const after = run.candidate_metrics
  if (!before || !after) return []
  const out: { key: string; before: number; after: number }[] = []
  for (const key of Object.keys(before)) {
    const b = (before as Record<string, unknown>)[key]
    const a = (after as Record<string, unknown>)[key]
    if (typeof b === 'number' && typeof a === 'number') out.push({ key, before: b, after: a })
  }
  return out
}

/**
 * 负向用例里**明确没拦住**的有几个。
 *
 * 判据只认一种写法：`passed === false`。契约里 `negative_cases` 的每一项是
 * 自由对象，**这里不去猜别的写法**——猜错的方向是致命的那个：
 * 把一次真的「没拦住」读成「都拦住了」。
 *
 * 所以 fail closed 反过来用：**看不出来就当没失败**，那句「有 N 种没拦住」
 * 是一句很重的话，说错了会让人去查一个不存在的漏洞；
 * 而它旁边的 `outcome` 标签本来就会把真的失败标红。
 */
function negativeCasesNotBlocked(run: ModuleEvaluationRun): number {
  if (!run.negative_cases) return 0
  return run.negative_cases.filter(
    (c) => typeof c === 'object' && c !== null && (c as Record<string, unknown>).passed === false,
  ).length
}

/** 把 scope 那个自由对象排成一行能读的字。 */
function describe(scope: Record<string, unknown> | undefined): string {
  if (!scope) return ''
  return Object.entries(scope)
    .map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`)
    .join(' · ')
}

/**
 * 契约里那些 `additionalProperties: true` 的 JSON。
 *
 * **原样排出来，不替它总结。** 这一层不知道 `problem_evidence` 里那个对象
 * 长什么样（契约刻意没定形状），编一句摘要出来就是凭空造话。
 */
function Json({ value }: { value: unknown }) {
  if (value === undefined || value === null) return null
  return (
    <pre className="m-0 overflow-x-auto rounded-npc-inset bg-npc-surface px-npc-gap-sm py-npc-gap-xs font-npc-mono text-npc-meta-sm text-npc-ink-medium">
      {JSON.stringify(value, null, 2)}
    </pre>
  )
}

function JsonList({ items, testId }: { items?: unknown[]; testId: string }) {
  if (!items || items.length === 0) return null
  return (
    <ol data-testid={testId} className="m-0 flex list-none flex-col gap-npc-gap-xs p-0">
      {items.map((item, i) => (
        <li key={i}>
          <Json value={item} />
        </li>
      ))}
    </ol>
  )
}
